// ════════════════════════════════════════════════════════════
// PedidosCliente — seguimiento de pedidos de cliente del almacén
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Alert, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  CircularProgress, Divider
} from '@mui/material';
import { getPedidosCliente, getPedidoCliente, cambiarEstadoPedido,
  descargarAlbaranPedido, actualizarEntregaPedido } from '../api';

const ESTADOS: any = {
  en_preparacion:     { label: 'En preparación',        color: 'warning' },
  enviado:            { label: 'Enviado',               color: 'info' },
  entregado:          { label: 'Entregado',             color: 'primary' },
  pendiente_facturar: { label: 'Pendiente de facturar', color: 'secondary' },
  facturado:          { label: 'Facturado',             color: 'success' },
  anulado:            { label: 'Anulado',               color: 'default' },
};
const ESTADO_LINEA: any = {
  disponible:       { label: 'Disponible',          color: 'success' },
  pendiente_compra: { label: 'Pendiente proveedor', color: 'warning' },
  recibida:         { label: 'Recibida',            color: 'info' },
};
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

export default function PedidosCliente({ onCambio }: { onCambio?: () => void }) {
  const [pedidos, setPedidos] = useState<any[]>([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [search, setSearch] = useState('');
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState<any>(null);
  const [envio, setEnvio] = useState<any>(null);
  const [procesando, setProcesando] = useState(false);
  const [entrega, setEntrega] = useState<any>({});

  const cargar = () => {
    setCargando(true);
    getPedidosCliente({ estado: filtroEstado, search })
      .then(setPedidos).catch(() => setPedidos([]))
      .finally(() => setCargando(false));
  };
  useEffect(() => {
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstado, search]);

  const abrir = async (id: number) => {
    try {
      const d = await getPedidoCliente(id);
      setDetalle(d);
      setEntrega({
        tipo_entrega: d.tipo_entrega || 'envio',
        direccion_envio: d.direccion_envio || '',
        transportista: d.transportista || '',
        recogido_por: d.recogido_por || '',
        recogido_dni: d.recogido_dni || '',
        recogido_matricula: d.recogido_matricula || '',
      });
    }
    catch { alert('No se pudo cargar el pedido'); }
  };

  const refrescar = async () => {
    cargar();
    onCambio?.();
    if (detalle) setDetalle(await getPedidoCliente(detalle.id));
  };

  const cambiar = async (datos: any): Promise<boolean> => {
    setProcesando(true);
    try {
      await cambiarEstadoPedido(detalle.id, datos);
      await refrescar();
      return true;
    } catch (e: any) {
      const msg = e.response?.data?.detail || 'Error al cambiar el estado';
      // Si faltan líneas del proveedor, permite enviar igualmente
      if (datos.estado === 'enviado' && !datos.forzar && msg.includes('pendientes')) {
        if (window.confirm(`${msg}.\n\n¿Enviar el pedido igualmente (envío parcial)?`)) {
          setProcesando(false);
          return cambiar({ ...datos, forzar: true });
        }
      } else {
        alert(msg);
      }
      return false;
    } finally {
      setProcesando(false);
    }
  };
  const guardarEntrega = async () => {
    if (!detalle) return;
    setProcesando(true);
    try {
      await actualizarEntregaPedido(detalle.id, entrega);
      alert('✅ Datos de entrega guardados');
    } catch (e: any) {
      alert(e.response?.data?.detail || 'No se pudo guardar');
    } finally { setProcesando(false); }
  };

  const confirmarEnvio = async () => {
    const ok = await cambiar({ estado: 'enviado',
      tipo_entrega: envio.tipo_entrega || 'envio',
      transportista: envio.transportista, numero_seguimiento: envio.numero_seguimiento,
      direccion_envio: envio.direccion_envio,
      recogido_por: envio.recogido_por, recogido_dni: envio.recogido_dni,
      recogido_matricula: envio.recogido_matricula, notas: envio.notas });
    if (ok) setEnvio(null);
  };

  const entregar = () => {
    if (!window.confirm(
      `¿Marcar el pedido ${detalle.numero} como entregado?\n\n` +
      `Se descontará el stock, se emitirá el albarán y pasará a Administración para facturar.`)) return;
    cambiar({ estado: 'entregado' });
  };

  const anular = () => {
    const motivo = window.prompt(`Anular el pedido ${detalle.numero}. Motivo:`);
    if (!motivo) return;
    cambiar({ estado: 'anulado', notas: motivo });
  };

  return (
    <Box>
      {/* Filtros */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Buscar (nº de pedido o cliente)"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Estado</InputLabel>
            <Select value={filtroEstado} label="Estado" onChange={(e) => setFiltroEstado(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(ESTADOS).map(([k, v]: any) =>
                <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {cargando ? <CircularProgress /> : pedidos.length === 0 ? (
        <Alert severity="info">
          No hay pedidos. Se crean al aceptar una oferta desde la pestaña Ofertas.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {pedidos.map((p: any) => {
            const est = ESTADOS[p.estado] || { label: p.estado, color: 'default' };
            return (
              <Grid item xs={12} sm={6} lg={4} key={p.id}>
                <Card onClick={() => abrir(p.id)}
                  sx={{ borderRadius: 3, cursor: 'pointer', height: '100%',
                    '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>{p.numero}</Typography>
                      <Chip size="small" label={est.label} color={est.color} />
                    </Box>
                    <Typography variant="body2">👤 {p.cliente_nombre}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      📅 {p.fecha} · {p.n_lineas} línea(s) · {eur(p.total)}
                    </Typography>
                    {p.n_pendientes > 0 && p.estado === 'en_preparacion' && (
                      <Chip size="small" color="warning" variant="outlined" sx={{ mt: 1 }}
                        label={`${p.n_pendientes} pendiente(s) de proveedor`} />
                    )}
                    {p.numero_seguimiento && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        🚚 {p.transportista || 'Envío'} · {p.numero_seguimiento}
                      </Typography>
                    )}
                    {['enviado', 'entregado', 'pendiente_facturar', 'facturado'].includes(p.estado) && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }} onClick={(e) => e.stopPropagation()}>
                        <Button size="small" variant="outlined"
                          onClick={() => descargarAlbaranPedido(p.id, p.numero, false)}>
                          📄 Albarán
                        </Button>
                        <Button size="small" variant="outlined"
                          onClick={() => descargarAlbaranPedido(p.id, p.numero, true)}>
                          💶 Valorado
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* DETALLE */}
      <Dialog open={!!detalle} maxWidth="md" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setDetalle(null); }}>
        {detalle && (() => {
          const est = ESTADOS[detalle.estado] || { label: detalle.estado, color: 'default' };
          return (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <span>🚚 Pedido {detalle.numero}</span>
                  <Chip label={est.label} color={est.color} />
                </Box>
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 0.5 }}>👤 <b>{detalle.cliente_nombre}</b></Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Fecha {detalle.fecha} · Total {eur(detalle.total)}
                  {detalle.condiciones_pago ? ` · ${detalle.condiciones_pago}` : ''}
                </Typography>

                {detalle.estado === 'pendiente_facturar' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Entregado. Pendiente de facturar desde <b>Administración</b>.
                  </Alert>
                )}
                {detalle.estado === 'facturado' && (
                  <Alert severity="success" sx={{ mb: 2 }}>Pedido facturado y cerrado.</Alert>
                )}

                <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, flex: 1 }}>Entrega</Typography>
                    {!['enviado', 'pendiente_facturar', 'facturado'].includes(detalle.estado) && (
                      <Button size="small" onClick={guardarEntrega} disabled={procesando}>
                        💾 Guardar
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Forma de entrega</InputLabel>
                      <Select label="Forma de entrega" value={entrega.tipo_entrega || 'envio'}
                        onChange={(e) => setEntrega({ ...entrega, tipo_entrega: e.target.value })}>
                        <MenuItem value="envio">🚚 Envío</MenuItem>
                        <MenuItem value="recogida">🙋 Recogida en almacén</MenuItem>
                      </Select>
                    </FormControl>
                    {(entrega.tipo_entrega || 'envio') === 'recogida' ? (
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField size="small" label="Recoge" sx={{ flex: 2, minWidth: 180 }}
                          value={entrega.recogido_por || ''}
                          onChange={(e) => setEntrega({ ...entrega, recogido_por: e.target.value })} />
                        <TextField size="small" label="DNI" sx={{ flex: 1, minWidth: 120 }}
                          value={entrega.recogido_dni || ''}
                          onChange={(e) => setEntrega({ ...entrega, recogido_dni: e.target.value })} />
                        <TextField size="small" label="Matrícula" sx={{ flex: 1, minWidth: 120 }}
                          value={entrega.recogido_matricula || ''}
                          onChange={(e) => setEntrega({ ...entrega, recogido_matricula: e.target.value })} />
                      </Box>
                    ) : (
                      <>
                        <TextField size="small" label="Dirección de envío" multiline rows={2}
                          helperText="Vacío = se usa la dirección fiscal del cliente"
                          value={entrega.direccion_envio || ''}
                          onChange={(e) => setEntrega({ ...entrega, direccion_envio: e.target.value })} />
                        <TextField size="small" label="Transportista"
                          value={entrega.transportista || ''}
                          onChange={(e) => setEntrega({ ...entrega, transportista: e.target.value })} />
                      </>
                    )}
                  </Box>
                </Box>

                <Typography sx={{ fontWeight: 700, mb: 1 }}>Líneas</Typography>
                <TableContainer sx={{ overflowX: 'auto', mb: 2 }}>
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Referencia</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Cant.</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalle.lineas.map((l: any) => {
                        const el = ESTADO_LINEA[l.estado_linea] || { label: l.estado_linea, color: 'default' };
                        return (
                          <TableRow key={l.id}>
                            <TableCell>{l.referencia || '—'}</TableCell>
                            <TableCell>{l.descripcion}</TableCell>
                            <TableCell align="right">{l.cantidad}</TableCell>
                            <TableCell align="right">{eur(l.cantidad * l.precio_unitario)}</TableCell>
                            <TableCell><Chip size="small" variant="outlined" label={el.label} color={el.color} /></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {detalle.pedidos_compra?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Pedidos a proveedor vinculados</Typography>
                    {detalle.pedidos_compra.map((pc: any) => (
                      <Typography key={pc.id} variant="body2">
                        📦 <b>{pc.numero}</b> — {pc.proveedor_nombre} · {pc.estado} · {eur(pc.total)}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Historial</Typography>
                {detalle.historial.map((h: any) => (
                  <Box key={h.id} sx={{ display: 'flex', gap: 1.5, mb: 0.75, alignItems: 'baseline' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 118 }}>
                      {h.fecha?.slice(0, 16)}
                    </Typography>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {ESTADOS[h.estado]?.label || h.estado}
                        {h.usuario_nombre ? ` · ${h.usuario_nombre}` : ''}
                      </Typography>
                      {h.notas && <Typography variant="caption" color="text.secondary">{h.notas}</Typography>}
                    </Box>
                  </Box>
                ))}
              </DialogContent>
              <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                {['en_preparacion', 'enviado'].includes(detalle.estado) && (
                  <Button color="error" onClick={anular} disabled={procesando}>Anular pedido</Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={() => descargarAlbaranPedido(detalle.id, detalle.numero, false)}>
                  📄 Albarán
                </Button>
                <Button onClick={() => descargarAlbaranPedido(detalle.id, detalle.numero, true)}>
                  💶 Albarán valorado
                </Button>
                <Button onClick={() => setDetalle(null)}>Cerrar</Button>
                {detalle.estado === 'en_preparacion' && (
                  <Button variant="outlined" disabled={procesando}
                    onClick={() => setEnvio({ transportista: '', numero_seguimiento: '', notas: '' })}>
                    🚚 Marcar enviado
                  </Button>
                )}
                {['en_preparacion', 'enviado'].includes(detalle.estado) && (
                  <Button variant="contained" onClick={entregar} disabled={procesando} sx={{ bgcolor: '#22c55e' }}>
                    ✅ Entregado
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ENVÍO */}
      <Dialog open={!!envio} maxWidth="xs" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setEnvio(null); }}>
        <DialogTitle>🚚 Datos del envío</DialogTitle>
        <DialogContent>
          {envio && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de entrega</InputLabel>
                <Select value={envio.tipo_entrega || 'envio'} label="Tipo de entrega"
                  onChange={(e) => setEnvio({ ...envio, tipo_entrega: e.target.value })}>
                  <MenuItem value="envio">🚚 Envío a dirección</MenuItem>
                  <MenuItem value="recogida">🙋 Recogida en almacén</MenuItem>
                </Select>
              </FormControl>

              {(envio.tipo_entrega || 'envio') === 'recogida' ? (
                <>
                  <TextField label="Recoge (nombre y apellidos)" value={envio.recogido_por || ''}
                    onChange={(e) => setEnvio({ ...envio, recogido_por: e.target.value })} />
                  <TextField label="DNI" value={envio.recogido_dni || ''}
                    onChange={(e) => setEnvio({ ...envio, recogido_dni: e.target.value })} />
                  <TextField label="Matrícula del vehículo" value={envio.recogido_matricula || ''}
                    onChange={(e) => setEnvio({ ...envio, recogido_matricula: e.target.value })} />
                </>
              ) : (
                <>
                  <TextField label="Dirección de envío" multiline rows={2}
                    helperText="Déjalo vacío para usar la dirección del cliente"
                    value={envio.direccion_envio || ''}
                    onChange={(e) => setEnvio({ ...envio, direccion_envio: e.target.value })} />
                  <TextField label="Transportista" value={envio.transportista || ''}
                    onChange={(e) => setEnvio({ ...envio, transportista: e.target.value })} />
                  <TextField label="Nº de seguimiento" value={envio.numero_seguimiento || ''}
                    onChange={(e) => setEnvio({ ...envio, numero_seguimiento: e.target.value })} />
                </>
              )}

              <TextField label="Notas (opcional)" multiline rows={2} value={envio.notas || ''}
                onChange={(e) => setEnvio({ ...envio, notas: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvio(null)}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarEnvio} disabled={procesando} sx={{ bgcolor: '#4f46e5' }}>
            Confirmar envío
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}